// before-after.ts — the compare card's live metrics, ported from the design attachment's
// anim-p3.js (`ba-charts`, lines 4786-4875).
//
// The drag/keyboard divider lives entirely in ../sections/before-after.tsx as ordinary React
// state (position%, clip-path derived from it) — the source's own `ba-slider` timeline (4878-4996)
// manipulated the DOM directly because it had no component tree to hold that state in; here it
// does, so this module owns only what Step 4 of the task actually asks for: the numbers.
//
// Two things this module deliberately does NOT port, both documented so a later task doesn't
// "fix" them by accident:
//   * the chart's own line redraw (source 4829-4841: an 80-sample sliding window plus a travelling
//     sine wave, redrawn as SVG points every frame). The section's polylines stay at the source's
//     own static resting frame (verbatim `points`) — only the odometer/packet-loss/jitter text the
//     brief calls out animates. The math below still needs the SAME seeded samples and sine wave
//     the redraw used, because the metric readouts are sampled off that exact curve (source
//     4849-4859) — it just never turns them into new `points` attributes.
//   * `ba-entry`'s reveal choreography (mirrored pane fade-in, chart path-draw, handle fade,
//     micro-pulse) — the source's own "no JS" contract (anim-p3.css's header comment: "sem JS a
//     página fica 100% visível e estática") already treats "everything painted at full opacity
//     immediately" as a valid resting state; that is what not registering a reveal produces here.
import type { SectionModule } from './use-section-animation';

const METRIC_STEP_MS = 800;
const CYAN_SAMPLE_COUNT = 80;
const CYAN_SEED = 31337;
const DOT_PULSE_PERIOD_MS = 2000;

// Source 4816: ten packet-loss steps for the "bad" pane, cycled in lockstep with the sampled
// point on the seeded curve below. The "good" pane's loss is always 0.0% (source 4862) — it is
// not a cycle, so it is passed as a literal rather than given its own array.
const BAD_LOSS_PERCENT: readonly number[] = [1.2, 8.4, 5.7, 15.3, 3.1, 12.6, 7.8, 19.4, 4.5, 20.0];

// Source 4175-4180 (np-engine.js `prng`): the same seeded LCG the mesh sections already reuse for
// their own jitter, ported verbatim so this lands on the exact sequence the design authored
// rather than a look-alike. The unsigned-32-bit truncation the LCG relies on needs `>>>`; the same
// escape hatch affiliates-hero-stars.ts and pricing-stars.ts already use for their own seeded PRNG.
/* eslint-disable no-bitwise */
function createPrng(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}
/* eslint-enable no-bitwise */

// Source 4793-4796: 80 samples in [122, 194], seeded once. Feeds only the bad-pane readouts below
// — see this file's header for why the chart itself never redraws from it.
function buildBadSamples(): readonly number[] {
  const random = createPrng(CYAN_SEED);
  const samples: number[] = [];
  for (let index = 0; index < CYAN_SAMPLE_COUNT; index += 1) samples.push(122 + random() * 72);
  return samples;
}

const BAD_SAMPLES = buildBadSamples();

// Source 4850, 4851: the good pane's analytic travelling wave, sampled at the same "graph x=800"
// edge the source reads its live readout from.
function goodWaveAt(sampleMs: number): number {
  return 8 + 1.35 * Math.sin((2 * Math.PI * (800 + sampleMs)) / 400) + 0.55 * Math.sin((2 * Math.PI * (800 + sampleMs) * 3) / 800);
}

// Source 4808-4812 (`makeOdo`): splits "156ms" into a live numeral plus its static unit, so only
// the digits re-render each step. Matches hero.ts's `.np-hero-main-number` split and the
// `.np3-live-value` rule this port's stylesheet already carries for it (tabular-nums, min-width).
function makeOdometer(element: HTMLElement): HTMLElement {
  // `element.textContent = …` on a callback parameter trips no-param-reassign; the alias is the
  // same escape hatch multi-internet.ts's own `setText` uses for its event-bar nodes.
  const node = element;
  const match = /^([\d.]+)/.exec(node.textContent ?? '');
  const unit = node.textContent?.slice(match?.[0]?.length ?? 0) ?? '';
  node.textContent = '';
  const value = document.createElement('span');
  value.className = 'np3-live-value';
  node.appendChild(value);
  node.appendChild(document.createTextNode(unit));
  return value;
}

export const beforeAfterAnimation: SectionModule = ({ root, strings, clock }) => {
  const badValue = root.querySelector<HTMLElement>('[data-anim="ba-avg-bad"]');
  const goodValue = root.querySelector<HTMLElement>('[data-anim="ba-avg-good"]');
  const badLoss = root.querySelector<HTMLElement>('[data-anim="ba-loss-bad"]');
  const badJitter = root.querySelector<HTMLElement>('[data-anim="ba-jitter-bad"]');
  const goodLoss = root.querySelector<HTMLElement>('[data-anim="ba-loss-good"]');
  const goodJitter = root.querySelector<HTMLElement>('[data-anim="ba-jitter-good"]');
  const badDot = root.querySelector<HTMLElement>('[data-anim="ba-live-dot"]');

  if (!badValue || !goodValue || !badLoss || !badJitter || !goodLoss || !goodJitter) return () => {};

  const badOdometer = makeOdometer(badValue);
  const goodOdometer = makeOdometer(goodValue);

  // `step` is a nested function declared after the guard above, and TS does not carry that
  // guard's null-narrowing into a nested closure (it widens `badLoss` etc. back to their
  // querySelector-declared, nullable types inside it) — hero.ts's `chart`/`hud` objects are the
  // same fix: rebind once, here, into an object TS infers as non-null from this exact assignment.
  const readouts = { badLoss, badJitter, goodLoss, goodJitter };

  function step(elapsedMs: number): void {
    const finite = Number.isFinite(elapsedMs);
    const t = finite ? elapsedMs : 0;

    // Source 4842-4848: the readout changes in clear 800ms steps, sampled off the underlying
    // curve at the start of that step — this is what keeps the numbers readable instead of
    // rolling frame by frame.
    const metricStep = Math.floor(t / METRIC_STEP_MS) % BAD_LOSS_PERCENT.length;
    const sampleMs = metricStep * 80;
    const badIndex = ((Math.floor(sampleMs / 10) % CYAN_SAMPLE_COUNT) + CYAN_SAMPLE_COUNT) % CYAN_SAMPLE_COUNT;
    const badEdge = BAD_SAMPLES[badIndex] ?? 0;
    const badNext = BAD_SAMPLES[(badIndex + 1) % CYAN_SAMPLE_COUNT] ?? 0;
    const badSlope = Math.abs(badNext - badEdge);
    const goodEdge = goodWaveAt(sampleMs);
    const goodSlope = Math.abs(goodEdge - goodWaveAt(sampleMs - 20));

    // Source 4855-4858.
    const badJitterMs = Math.max(12, Math.min(78, 12 + (badEdge - 122) * 0.45 + badSlope * 0.34));
    const goodJitterMs = Math.max(0.6, Math.min(1.8, 0.7 + (goodEdge - 6.1) * 0.24 + goodSlope * 0.12));
    const badLossPercent = BAD_LOSS_PERCENT[metricStep] ?? 0;

    badOdometer.textContent = String(Math.round(badEdge));
    goodOdometer.textContent = goodEdge.toFixed(1);
    readouts.badLoss.textContent = strings.beforeAfter.packetLoss(badLossPercent);
    readouts.badJitter.textContent = strings.beforeAfter.jitter(badJitterMs, 0);
    readouts.goodLoss.textContent = strings.beforeAfter.packetLoss(0);
    readouts.goodJitter.textContent = strings.beforeAfter.jitter(goodJitterMs, 1);

    // Source 4858-4859: the "Without NoPing" status dot pulses on the engine's clock instead of
    // Tailwind's wall-clock `animate-pulse` (trial-beta.css's `.trial-beta-page.np
    // #before-after .animate-pulse { animation: none }` already turns the CSS one off once the
    // engine is alive — this replaces it, exactly like hero.ts's kicker dot).
    if (badDot) badDot.style.opacity = finite ? (0.72 + 0.28 * Math.cos((2 * Math.PI * t) / DOT_PULSE_PERIOD_MS)).toFixed(3) : '1';
  }

  // Paint the resting frame before the first tick (same convention as hero.ts/multi-internet.ts),
  // then let the shared clock drive every subsequent frame.
  step(0);
  const unsubscribe = clock.subscribe(step);

  return () => {
    unsubscribe();
  };
};
